import React, { useState, useEffect, useRef, useCallback } from "react";
import { Navigate } from "react-router-dom";
import {
  Sparkles, Send, Plus, MessageSquare, Trash2, ShieldCheck, CheckCircle2,
  XCircle, ScanLine, FileText, Loader2, Bot, User as UserIcon, Building2,
} from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader } from "../lib/ui";
import { useT } from "../lib/i18n";
import { useToast } from "../lib/toast";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const rp = (v) => (v == null ? "-" : "Rp " + Math.round(Number(v)).toLocaleString("id-ID"));

// ---------------- Minimal markdown (bold / bullets / headings) ----------------
function inlineMd(s, k) {
  return String(s).split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={`${k}-${i}`} className="font-bold text-ink">{p.slice(2, -2)}</strong>
      : <span key={`${k}-${i}`}>{p}</span>);
}
function Markdown({ text }) {
  const lines = String(text || "").split("\n");
  const out = []; let list = [];
  const flush = (k) => { if (list.length) { out.push(<ul key={`ul-${k}`} className="my-1.5 ml-4 list-disc space-y-1">{list.map((l, i) => <li key={i}>{inlineMd(l, `li-${k}-${i}`)}</li>)}</ul>); list = []; } };
  lines.forEach((raw, idx) => {
    const line = raw.replace(/\s+$/, "");
    if (/^#{1,6}\s/.test(line)) { flush(idx); out.push(<p key={idx} className="mb-1 mt-2 font-heading text-sm font-bold text-ink">{inlineMd(line.replace(/^#{1,6}\s/, ""), `h-${idx}`)}</p>); }
    else if (/^\s*[-*]\s+/.test(line)) list.push(line.replace(/^\s*[-*]\s+/, ""));
    else if (/^\s*\d+[).]\s+/.test(line)) list.push(line.replace(/^\s*\d+[).]\s+/, ""));
    else if (line.trim() === "") { flush(idx); out.push(<div key={idx} className="h-1.5" />); }
    else { flush(idx); out.push(<p key={idx} className="my-0.5">{inlineMd(line, `p-${idx}`)}</p>); }
  });
  flush("end");
  return <div className="text-sm leading-relaxed text-ink">{out}</div>;
}

const OP_STATUS_TONE = { pending: "warn", pending_approval: "warn", awaiting_owner: "warn", flagged_fraud: "danger", executing: "warn", executed: "success", failed: "danger", rejected: "mute", invalid: "danger" };
const OP_STATUS_LABEL = { pending: "Menunggu Konfirmasi", pending_approval: "Menunggu Konfirmasi Owner", awaiting_owner: "Menunggu Persetujuan Owner", flagged_fraud: "Ditandai: Potensi Fraud", executing: "Memproses…", executed: "Dieksekusi", failed: "Gagal", rejected: "Ditolak", invalid: "Tidak Valid" };

// ---------------- Operation preview card ----------------
function OpCard({ op, onConfirm, onReject, onSetDestination, busy, isOwner }) {
  const pv = op.preview || {};
  const isOcr = op.kind === "ocr_invoice";
  const err = pv.error || op.error;
  return (
    <div className="ml-10 mt-2 rounded-2xl border border-line bg-stone-50 p-4" data-testid={`ai-op-${op.id}`}>
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          {isOcr ? <ScanLine size={16} className="text-primary" /> : <Building2 size={16} className="text-primary" />}
          <p className="font-heading text-sm font-bold text-ink">{pv.title || op.action}</p>
        </div>
        <Badge tone={OP_STATUS_TONE[op.status] || "mute"} data-testid={`ai-op-status-${op.id}`}>
          {OP_STATUS_LABEL[op.status] || op.status}
        </Badge>
      </div>
      {pv.summary && <p className="mb-2 text-xs text-ink-soft">{pv.summary}</p>}
      {err && <p className="mb-2 rounded-lg bg-danger-light px-2.5 py-1.5 text-xs text-danger-text" data-testid={`ai-op-error-${op.id}`}>{err}</p>}

      {Array.isArray(pv.fields) && pv.fields.length > 0 && (
        <div className="mb-2 grid grid-cols-2 gap-x-4 gap-y-1">
          {pv.fields.map((f, i) => (
            <div key={i} className="flex justify-between gap-2 text-xs">
              <span className="text-ink-mute">{f.label}</span>
              <span className="font-semibold text-ink">{String(f.value)}</span>
            </div>
          ))}
        </div>
      )}

      {isOcr && Array.isArray(pv.items) && (
        <div className="mb-2 overflow-hidden rounded-lg border border-line">
          <table className="w-full text-xs">
            <thead className="bg-white text-ink-mute">
              <tr><th className="px-2 py-1 text-left">Item</th><th className="px-2 py-1 text-right">Qty</th><th className="px-2 py-1 text-right">Harga</th><th className="px-2 py-1 text-left">Cocok</th></tr>
            </thead>
            <tbody>
              {pv.items.map((it, i) => (
                <tr key={i} className="border-t border-line bg-white">
                  <td className="px-2 py-1">{it.ocr_name}</td>
                  <td className="px-2 py-1 text-right">{it.quantity} {it.unit}</td>
                  <td className="px-2 py-1 text-right">{rp(it.unit_price)}</td>
                  <td className="px-2 py-1">
                    {it.matched_product_name
                      ? <span className="text-success-text">{it.matched_product_name}</span>
                      : <span className="text-danger-text">Belum cocok</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {isOcr && op.status === "pending" && Array.isArray(pv.destination_options) && pv.destination_options.length > 0 && (
        <div className="mb-2" data-testid={`ai-op-dest-${op.id}`}>
          <p className="mb-1 text-[11px] font-semibold text-ink-mute">Posting ke:</p>
          <div className="flex flex-wrap gap-1.5">
            {pv.destination_options.map((o) => {
              const active = (pv.destination || "central") === o.value;
              return (
                <button key={o.value} type="button" disabled={busy === op.id}
                  data-testid={`ai-op-dest-${op.id}-${o.value}`}
                  onClick={() => onSetDestination && onSetDestination(op, o.value)}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${active ? "border-primary bg-primary-light text-primary" : "border-line bg-white text-ink-soft hover:border-primary/40"}`}>
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {isOcr && <p className="mb-2 text-[11px] text-ink-mute">Tujuan: <span className="font-semibold text-ink" data-testid={`ai-op-dest-label-${op.id}`}>{pv.destination_label || "Gudang Pusat"}</span> · Total {rp(pv.total_value)}</p>}

      {Array.isArray(pv.warnings) && pv.warnings.map((w, i) => (
        <p key={i} className="mb-1 text-[11px] text-warn-text">⚠ {w}</p>
      ))}

      {op.status === "pending" && (
        <div className="mt-2 flex gap-2">
          <Button size="sm" onClick={() => onConfirm(op)} loading={busy === op.id} data-testid={`ai-op-confirm-${op.id}`}>
            <CheckCircle2 size={14} /> {(!isOwner && op.review_mode === "owner") ? "Ajukan ke Owner" : "Konfirmasi & Jalankan"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => onReject(op)} disabled={busy === op.id} data-testid={`ai-op-reject-${op.id}`}>
            <XCircle size={14} /> Tolak
          </Button>
        </div>
      )}
      {op.status === "awaiting_owner" && (
        <div className="mt-2 rounded-lg bg-warn-light px-2.5 py-1.5 text-[11px] text-warn-text" data-testid={`ai-op-awaiting-owner-${op.id}`}>
          ⏳ Menunggu <strong>Persetujuan Owner</strong>. Aksi ini muncul di menu <strong>Confirmation Action</strong> milik Owner. Stok belum berubah.
        </div>
      )}
      {op.status === "flagged_fraud" && (
        <div className="mt-2 rounded-lg bg-danger-light px-2.5 py-1.5 text-[11px] text-danger-text" data-testid={`ai-op-flagged-fraud-${op.id}`}>
          🚩 Ditandai <strong>Potensi Fraud</strong> oleh Owner — sedang ditinjau. Stok belum berubah.
        </div>
      )}
      {op.status === "executed" && op.result?.reference_no && (
        <p className="mt-1 text-xs text-success-text">✓ {op.result.reference_no}</p>
      )}
      {op.status === "executed" && op.result?.after != null && (
        <p className="mt-1 text-xs text-success-text" data-testid={`ai-op-applied-${op.id}`}>
          ✓ Stok diperbarui: {op.result.before} → {op.result.after} {op.result.unit || ""}
          {op.result.approved_by_name ? ` · disetujui ${op.result.approved_by_name}` : ""}
        </p>
      )}
      {op.status === "pending_approval" && (
        <div className="mt-2 rounded-lg bg-warn-light px-2.5 py-1.5 text-[11px] text-warn-text" data-testid={`ai-op-pending-approval-${op.id}`}>
          ⏳ Melebihi ambang — menunggu <strong>Konfirmasi Owner</strong> di menu <strong>Confirmation Action</strong> (engine Waste ERP). <strong>Stok belum berubah</strong>
          {op.result?.before != null ? ` (${op.result.before} → ${op.result.after} ${op.result.unit || ""})` : ""}.
          Status &amp; stok diperbarui otomatis setelah Owner menyetujui.
        </div>
      )}
    </div>
  );
}

const TOPIC_SUGGESTIONS = {
  inventory: "Stok apa yang statusnya kritis?",
  low_stock: "Produk apa yang hampir habis?",
  recipes: "Tampilkan resep untuk menu andalan.",
  sales: "Menu terlaris bulan ini?",
  purchasing: "Berapa total pembelian bulan ini?",
  margin_cogs: "Produk mana yang marginnya paling rendah?",
  price_history: "Produk apa yang harganya naik paling sering?",
  finance: "Bagaimana kondisi arus kas kita?",
  production: "Berapa produksi minggu ini?",
  branch_overview: "Ringkasan performa cabang saya?",
  suppliers: "Supplier mana yang paling sering dipakai?",
  transfers: "Transfer terakhir ke cabang apa saja?",
};

export default function CentralFlowAI() {
  const { t } = useT();
  const toast = useToast();
  const { user } = useAuth();
  const [caps, setCaps] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [ops, setOps] = useState({});          // id -> op
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [opBusy, setOpBusy] = useState(null);
  const scrollRef = useRef(null);
  const invoiceInput = useRef(null);
  const bankInput = useRef(null);
  const receiptInput = useRef(null);

  const loadSessions = useCallback(async () => {
    try { const { data } = await api.get("/ai-assistant/sessions"); setSessions(data.sessions || []); }
    catch { /* noop */ }
  }, []);

  useEffect(() => {
    api.get("/ai-assistant/capabilities").then(({ data }) => setCaps(data)).catch(() => {});
    loadSessions();
  }, [loadSessions]);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, loading, ops]);

  const openSession = useCallback(async (sid) => {
    setActiveId(sid);
    try {
      const { data } = await api.get(`/ai-assistant/sessions/${sid}`);
      setMessages(data.messages || []);
      const m = {}; (data.operations || []).forEach((o) => { m[o.id] = o; }); setOps(m);
    } catch { toast.error(t("Failed to load")); }
  }, [t, toast]);

  const newSession = useCallback(async () => {
    try {
      const { data } = await api.post("/ai-assistant/sessions", {});
      setSessions((s) => [{ id: data.id, title: data.title }, ...s]);
      setActiveId(data.id); setMessages([]); setOps({});
    } catch { toast.error(t("Something went wrong")); }
  }, [t, toast]);

  const ensureSession = useCallback(async () => {
    if (activeId) return activeId;
    const { data } = await api.post("/ai-assistant/sessions", {});
    setSessions((s) => [{ id: data.id, title: data.title }, ...s]);
    setActiveId(data.id);
    return data.id;
  }, [activeId]);

  const sendPayload = useCallback(async (payload, displayText) => {
    if (loading) return;
    setLoading(true);
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content: displayText }]);
    try {
      const sid = await ensureSession();
      const { data } = await api.post(`/ai-assistant/sessions/${sid}/message`, payload);
      setMessages((m) => [...m, data.assistant_message]);
      if (data.operations?.length) setOps((o) => { const n = { ...o }; data.operations.forEach((op) => { n[op.id] = op; }); return n; });
      if (!sessions.find((s) => s.id === sid)?.title) loadSessions();
    } catch (e) {
      const msg = e?.response?.data?.detail || t("Something went wrong");
      toast.error(msg);
      setMessages((m) => [...m, { id: `err-${Date.now()}`, role: "assistant", content: msg }]);
    } finally { setLoading(false); }
  }, [loading, ensureSession, sessions, loadSessions, t, toast]);

  const send = useCallback((q) => {
    const text = (q ?? input).trim();
    if (!text) return;
    setInput("");
    sendPayload({ message: text }, text);
  }, [input, sendPayload]);

  const onFile = useCallback((file, kind) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result);
      if (kind === "invoice") sendPayload({ message: "Baca faktur ini dan siapkan pembelian.", doc_kind: "invoice", image_base64: b64 }, `📄 Faktur: ${file.name}`);
      else if (kind === "receipt") sendPayload({ message: "Baca bon/struk ini dan catat penjualan POS cabang saya. Cocokkan ke menu yang ada; tanya bila ragu.", image_base64: b64 }, `🧾 Bon: ${file.name}`);
      else sendPayload({ message: "Baca dan analisis rekening koran ini.", doc_kind: "bank_statement", file_base64: b64, file_name: file.name }, `🏦 Rekening koran: ${file.name}`);
    };
    reader.readAsDataURL(file);
  }, [sendPayload]);

  const confirmOp = useCallback(async (op) => {
    setOpBusy(op.id);
    try {
      const { data } = await api.post(`/ai-assistant/operations/${op.id}/confirm`);
      setOps((o) => ({ ...o, [op.id]: { ...o[op.id], status: data.status, result: data.result, error: data.error } }));
      if (data.status === "executed") toast.success(t("Success"));
      else toast.error(data.error || t("Something went wrong"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("Something went wrong")); }
    finally { setOpBusy(null); }
  }, [t, toast]);

  const rejectOp = useCallback(async (op) => {
    setOpBusy(op.id);
    try {
      await api.post(`/ai-assistant/operations/${op.id}/reject`);
      setOps((o) => ({ ...o, [op.id]: { ...o[op.id], status: "rejected" } }));
    } catch (e) { toast.error(e?.response?.data?.detail || t("Something went wrong")); }
    finally { setOpBusy(null); }
  }, [t, toast]);

  const setOpDestination = useCallback(async (op, destination) => {
    setOpBusy(op.id);
    try {
      const { data } = await api.post(`/ai-assistant/operations/${op.id}/set-destination`, { destination });
      setOps((o) => ({ ...o, [op.id]: { ...o[op.id], ...data } }));
    } catch (e) { toast.error(e?.response?.data?.detail || t("Something went wrong")); }
    finally { setOpBusy(null); }
  }, [t, toast]);

  const deleteSession = useCallback(async (sid, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/ai-assistant/sessions/${sid}`);
      setSessions((s) => s.filter((x) => x.id !== sid));
      if (activeId === sid) { setActiveId(null); setMessages([]); setOps({}); }
    } catch { toast.error(t("Something went wrong")); }
  }, [activeId, t, toast]);

  const suggestions = (caps?.allowed_topics || []).map((tp) => TOPIC_SUGGESTIONS[tp]).filter(Boolean).slice(0, 6);

  if (user && !["OWNER", "SUPER_ADMIN", "KITCHEN", "BRANCH_STAFF"].includes(user.role)) return <Navigate to="/" replace />;

  return (
    <div data-testid="central-flow-ai-page">
      <PageHeader title="Central Flow AI Assistant"
        subtitle={caps?.branch_scoped ? `ChatGPT · Cabang ${caps?.branch_name}` : "ChatGPT · Asisten operasional, keuangan & dokumen"}
        icon={Sparkles} />

      <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
        {/* Sessions sidebar */}
        <div className="hidden lg:block">
          <Button className="mb-3 w-full" onClick={newSession} data-testid="ai-new-session"><Plus size={16} /> Chat Baru</Button>
          <div className="space-y-1">
            {sessions.map((s) => (
              <button key={s.id} onClick={() => openSession(s.id)} data-testid={`ai-session-${s.id}`}
                className={`group flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm transition-colors ${activeId === s.id ? "bg-primary-light text-primary" : "text-ink-soft hover:bg-stone-100"}`}>
                <MessageSquare size={15} className="shrink-0" />
                <span className="flex-1 truncate">{s.title || "Chat baru"}</span>
                <Trash2 size={14} className="shrink-0 opacity-0 hover:text-danger-text group-hover:opacity-100" onClick={(e) => deleteSession(s.id, e)} />
              </button>
            ))}
            {sessions.length === 0 && <p className="px-3 py-2 text-xs text-ink-mute">Belum ada percakapan.</p>}
          </div>
        </div>

        {/* Chat column */}
        <Card className="flex h-[calc(100vh-240px)] min-h-[460px] flex-col p-0" data-testid="ai-chat-card">
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-5 sm:px-6" data-testid="ai-messages">
            {messages.length === 0 && (
              <div className="mx-auto max-w-2xl py-4 text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-light text-primary"><Sparkles size={26} /></div>
                <h3 className="font-heading text-lg font-bold text-ink">Tanyakan apa saja tentang Central Flow</h3>
                <p className="mt-1 text-sm text-ink-soft">Analisis data, baca faktur & rekening koran, dan bantu alur kerja ERP — semua dalam satu percakapan, sesuai wewenang Anda.</p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {suggestions.map((qq, qi) => (
                    <button key={qi} data-testid={`ai-suggestion-${qi}`} onClick={() => send(qq)}
                      className="rounded-full border border-line bg-white px-3 py-1.5 text-xs font-medium text-ink-soft transition-colors hover:border-primary/40 hover:bg-primary-light hover:text-primary">{qq}</button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => {
              const msgOps = (m.operation_ids || []).map((id) => ops[id]).filter(Boolean);
              return m.role === "user" ? (
                <div key={m.id || i} className="flex justify-end" data-testid="ai-message-user">
                  <div className="flex max-w-[85%] items-end gap-2">
                    <div className="rounded-2xl rounded-br-md bg-primary px-4 py-2.5 text-sm font-medium text-white shadow-sm">{m.content}</div>
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-white"><UserIcon size={15} /></div>
                  </div>
                </div>
              ) : (
                <div key={m.id || i} data-testid="ai-message-assistant">
                  <div className="flex gap-2.5">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-light text-primary"><Bot size={16} /></div>
                    <div className="max-w-[88%] rounded-2xl rounded-tl-md border border-line bg-white px-4 py-3 shadow-sm"><Markdown text={m.content} /></div>
                  </div>
                  {msgOps.map((op) => <OpCard key={op.id} op={op} onConfirm={confirmOp} onReject={rejectOp} onSetDestination={setOpDestination} busy={opBusy} isOwner={["OWNER","SUPER_ADMIN"].includes(user?.role)} />)}
                </div>
              );
            })}

            {loading && (
              <div className="flex gap-2.5" data-testid="ai-loading">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-light text-primary"><Bot size={16} /></div>
                <div className="flex items-center gap-2 rounded-2xl rounded-tl-md border border-line bg-white px-4 py-3 text-sm text-ink-soft shadow-sm"><Spinner /> ChatGPT sedang memproses…</div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-line bg-white p-3 sm:p-4">
            <form className="flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); send(); }}>
              {user?.role === "BRANCH_STAFF" && (
                <>
                  <input ref={receiptInput} type="file" accept="image/*" capture="environment" className="hidden" data-testid="ai-receipt-file"
                    onChange={(e) => { onFile(e.target.files?.[0], "receipt"); e.target.value = ""; }} />
                  <Button type="button" variant="outline" size="md" onClick={() => receiptInput.current?.click()} disabled={loading} data-testid="ai-scan-receipt" title="Foto Bon / Struk">
                    <ScanLine size={16} /> <span className="hidden sm:inline">Foto Bon</span>
                  </Button>
                </>
              )}
              {caps?.can_scan_invoice && (
                <>
                  <input ref={invoiceInput} type="file" accept="image/*" className="hidden" data-testid="ai-invoice-file"
                    onChange={(e) => { onFile(e.target.files?.[0], "invoice"); e.target.value = ""; }} />
                  <Button type="button" variant="outline" size="md" onClick={() => invoiceInput.current?.click()} disabled={loading} data-testid="ai-scan-invoice" title="Scan Faktur">
                    <ScanLine size={16} /> <span className="hidden sm:inline">Faktur</span>
                  </Button>
                </>
              )}
              {caps?.can_read_bank_statement && (
                <>
                  <input ref={bankInput} type="file" accept=".csv,.xls,.xlsx,.pdf" className="hidden" data-testid="ai-bank-file"
                    onChange={(e) => { onFile(e.target.files?.[0], "bank"); e.target.value = ""; }} />
                  <Button type="button" variant="outline" size="md" onClick={() => bankInput.current?.click()} disabled={loading} data-testid="ai-read-bank" title="Baca Rekening Koran">
                    <FileText size={16} /> <span className="hidden sm:inline">Bank</span>
                  </Button>
                </>
              )}
              <textarea data-testid="ai-chat-input" value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                rows={1} placeholder="Tulis pertanyaan… mis. Produk apa yang hampir habis?"
                className="cf-input max-h-28 flex-1 resize-none" />
              <Button type="submit" loading={loading} disabled={!input.trim()} data-testid="ai-send-button"><Send size={16} /></Button>
            </form>
            <div className="mt-2 flex items-center gap-1.5 text-[11px] text-ink-mute" data-testid="ai-safety-note">
              <ShieldCheck size={13} /> Perubahan data selalu lewat Preview → Konfirmasi → Eksekusi. AI tidak pernah menulis langsung & hanya melihat data sesuai wewenang Anda.
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
